import apiService from './apiService';

// Calendar provider types
export type CalendarProvider = 'google' | 'apple' | 'outlook' | 'microsoft';

export const calendarService = {
  /**
   * Get OAuth URL for calendar provider
   */
  async getOAuthUrl(provider: string): Promise<{ url: string }> {
    return apiService.get(`/meetings/calendar/oauth-url/${provider}`);
  },

  /**
   * Get user's calendar integrations
   */
  async getIntegrations(): Promise<any> {
    return apiService.get('/meetings/calendar/integrations');
  },

  /**
   * Connect a calendar provider (Google, Apple)
   */
  async connectCalendar(provider: string, code: string, redirectUri: string): Promise<any> {
    return apiService.post('/meetings/calendar/connect', {
      provider,
      code,
      redirectUri,
    });
  },

  /**
   * Disconnect a calendar integration
   */
  async disconnectCalendar(integrationId: string): Promise<void> {
    return apiService.delete(`/meetings/calendar/${integrationId}`);
  },

  /**
   * Sync calendar events from a connected provider
   */
  async syncCalendar(integrationId: string): Promise<any> {
    return apiService.post(`/meetings/calendar/${integrationId}/sync`);
  },

  /**
   * Get user's calendar events
   */
  async getCalendarEvents(params?: {
    startDate?: string;
    endDate?: string;
    source?: string;
  }): Promise<any> {
    return apiService.get('/meetings/calendar/events', { params });
  },
};

/**
 * Helper function to check if calendar integration was successful
 */
const checkIntegrationStatus = async (
  provider: 'google' | 'apple',
  _userId: string
): Promise<boolean> => {
  try {
    // Wait a bit for backend to process
    await new Promise((resolve) => setTimeout(resolve, 3000));
    const integrations = await calendarService.getIntegrations();
    const integration = integrations.find((i: any) => i.provider === provider);
    return !!integration;
  } catch (error) {
    console.error('Error checking integration status:', error);
    return false;
  }
};

// LocalStorage key for OAuth communication (bypasses COOP restrictions)
const OAUTH_RESULT_KEY = 'calendar_oauth_result';

/**
 * Helper function to initiate Google Calendar OAuth flow
 * Uses popup flow for better UX
 * Uses localStorage for cross-origin communication (COOP workaround)
 */
export async function connectCalendarProvider(
  provider: 'google' | 'apple',
  _token: string,
  userId: string
): Promise<boolean> {
  try {
    // Clear any previous OAuth result
    localStorage.removeItem(OAUTH_RESULT_KEY);

    // Get OAuth URL from backend
    const { url } = await calendarService.getOAuthUrl(provider);

    // Open the authorization URL in a popup
    const popup = window.open(
      url,
      'Calendar Authorization',
      'width=600,height=700'
    );

    if (!popup) {
      console.error('Popup blocked. Please allow popups for this site.');
      return false;
    }

    // Create a promise that resolves when OAuth completes
    return new Promise<boolean>((resolve) => {
      let resolved = false;
      let checkInterval: ReturnType<typeof setInterval> | null = null;

      const cleanup = () => {
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
        window.removeEventListener('message', messageHandler);
        window.removeEventListener('storage', storageHandler);
      };

      const resolveOnce = (value: boolean) => {
        if (!resolved) {
          resolved = true;
          cleanup();
          localStorage.removeItem(OAUTH_RESULT_KEY);
          resolve(value);
        }
      };

      // Listen for message from OAuth callback (works if COOP doesn't block)
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === 'calendar-oauth-callback') {
          const { code, error } = event.data;

          if (error) {
            console.error('OAuth error:', error);
            resolveOnce(false);
            return;
          }

          if (code) {
            const redirectUri = `${window.location.origin}/calendar/callback`;
            calendarService
              .connectCalendar(provider, code, redirectUri)
              .then(() => {
                resolveOnce(true);
              })
              .catch((err) => {
                // 409 Conflict means calendar is already connected - treat as success
                if (err?.response?.status === 409) {
                  resolveOnce(true);
                  return;
                }
                console.error('Error connecting calendar:', err);
                resolveOnce(false);
              });
          } else {
            resolveOnce(false);
          }
        }
      };

      // Listen for localStorage changes (COOP-safe fallback)
      const storageHandler = (event: StorageEvent) => {
        if (event.key === OAUTH_RESULT_KEY && event.newValue) {
          try {
            const result = JSON.parse(event.newValue);
            if (result.success) {
              resolveOnce(true);
            } else if (result.error) {
              console.error('OAuth error from storage:', result.error);
              resolveOnce(false);
            }
          } catch {
            // Invalid JSON, ignore
          }
        }
      };

      window.addEventListener('message', messageHandler);
      window.addEventListener('storage', storageHandler);

      // Poll for localStorage changes and check integration status
      // NOTE: We don't check popup.closed because Google OAuth uses COOP which blocks it
      let pollCount = 0;
      const maxPolls = 300; // 5 minutes at 1 second intervals
      let integrationCheckScheduled = false;

      checkInterval = setInterval(() => {
        pollCount++;

        // Check localStorage for result (primary method - works with COOP)
        const storedResult = localStorage.getItem(OAUTH_RESULT_KEY);
        if (storedResult) {
          try {
            const result = JSON.parse(storedResult);
            if (result.success) {
              resolveOnce(true);
              return;
            } else if (result.error) {
              console.error('OAuth error from storage:', result.error);
              resolveOnce(false);
              return;
            }
          } catch {
            // Invalid JSON, continue polling
          }
        }

        // After 15 seconds, start checking integration status periodically
        // This handles cases where localStorage communication fails
        if (pollCount >= 15 && pollCount % 5 === 0 && !integrationCheckScheduled) {
          integrationCheckScheduled = true;
          checkIntegrationStatus(provider, userId).then((success) => {
            integrationCheckScheduled = false;
            if (success) {
              resolveOnce(true);
            }
          });
        }

        // Timeout after max polls
        if (pollCount >= maxPolls) {
          checkIntegrationStatus(provider, userId).then((success) => {
            resolveOnce(success);
          });
        }
      }, 1000);
    });
  } catch (error) {
    console.error('Error connecting calendar:', error);
    return false;
  }
}
